<script>
import UserResource from '@/resources/user'
import LapTimeResource from '@/resources/laptime'
import GroupResource from '@/resources/group'
import TrackResource from '@/resources/track'
import messages from '@/mixins/messages'
import { strMaxLength } from '@/util'

export default {
    name: 'Laps',
    mixins: [messages],
    components: {},
    computed: {
        lapsResources() {
            return this.lapsPage.resources
        },
        lapsFilters() {
            return {
                user: this.$store.getters.userId,
                approved: this.validLapsOnly ? true : '',
                search: this.lapTimeQuery,
                track: this.selectedTrack,
                car__groups__id: this.selectedGroup,
                ordering: this.ranked ? 'rank' : '-created',
            }
        },
        ranked() {
            if (this.selectedGroup && this.selectedTrack && this.validLapsOnly) {
                return true
            }

            return false
        },
        selectedTrackNameShort() {
            return strMaxLength(this.selectedTrackName, 12)
        },
        hasFilters() {
            return !!(this.lapTimeQuery || this.selectedGroup || this.selectedTrack || this.validLapsOnly)
        },
        pageLabel() {
            if (!this.lapsPage || !this.lapsPage.pages || this.lapsPage.pages < 2) {
                return ''
            }
            return `Page ${this.lapsPage.page} of ${this.lapsPage.pages}`
        },
    },
    data() {
        return {
            userResource: null,
            lapsPage: null,
            lapsNoneReorded: false,
            lapTimeQuery: '',
            lapsLoading: false,
            calendarResource: null,
            groupResources: [],
            trackResources: [],
            selectedGroup: '',
            selectedTrack: '',
            validLapsOnly: false,
            selectedTrackName: '',
            loading: true,
        }
    },
    methods: {
        lapMeta(lapTime) {
            const parts = []
            if (lapTime.attributes.tire) {
                parts.push(`tire "${lapTime.attributes.tire}"`)
            }
            if (lapTime.attributes.fuel) {
                parts.push(`${parseInt(lapTime.attributes.fuel)}L fuel`)
            }
            if (lapTime.attributes.ballast) {
                parts.push(`ballast ${lapTime.attributes.ballast}`)
            }
            return parts.filter(Boolean).join('  ·  ')
        },
        async getLaps(options = {}) {
            this.lapsLoading = true
            this.lapsPage = await LapTimeResource.filter(this.lapsFilters)
            this.lapsLoading = false

            if (options.initial && !this.lapsPage.resources.length) {
                this.lapsNoneReorded = true
            }

            const query = {
                ...this.$route.query,
                group: this.selectedGroup,
                track: this.selectedTrack,
            }

            try {
                await this.$router.replace({ name: this.$route.name, query })
            } catch (e) {
                // Do nothing
            }
        },
        async lapTimePrevPage() {
            this.lapsPage = await this.lapsPage.prevPage()
            window.scrollTo(0, 0)
        },
        async lapTimeNextPage() {
            this.lapsPage = await this.lapsPage.nextPage()
            window.scrollTo(0, 0)
        },
        async clearLapFilters() {
            this.lapTimeQuery = ''
            this.selectedGroup = ''
            this.selectedTrack = ''
            this.selectedTrackName = ''
            this.validLapsOnly = false
            await this.getLaps()
        },
        async selectTrackAndGo(trackId, trackName) {
            this.selectedTrack = trackId
            this.selectedTrackName = trackName
            this.validLapsOnly = true

            if (!this.selectedGroup) {
                this.selectedGroup = 'GT3'
            }

            await this.getLaps()
        },
        modalSelectGroup(groupId) {
            this.selectedGroup = groupId
            this.$refs.groupModal.hide()
            this.getLaps()
        },
        modalSelectTrack(trackId, trackName) {
            this.selectedTrack = trackId
            this.selectedTrackName = trackName
            this.$refs.trackModal.hide()
            this.getLaps()
        },
    },
    async mounted() {
        this.loading = true
        let initial = true

        const [usersResult, groupsResult, tracksResult] = await Promise.all([await UserResource.detail(this.$store.getters.userId), await GroupResource.all(), await TrackResource.all()])

        this.userResource = usersResult
        this.groupResources = groupsResult
        this.trackResources = tracksResult

        if (this.$route.query.group) {
            this.selectedGroup = this.$route.query.group
            initial = false
        }

        if (this.$route.query.track) {
            const queryTrack = this.trackResources.find((trackResource) => trackResource.id == this.$route.query.track)

            if (queryTrack) {
                this.selectedTrack = queryTrack.id
                this.selectedTrackName = queryTrack.attributes.name
                initial = false
            }
        }

        await this.getLaps({ initial })
        this.loading = false
    },
}
</script>

<template>
    <div class="sa-page">
        <header-image kicker="Télémétrie personnelle"><h1>Mes temps au tour</h1></header-image>

        <div class="sa-page__inner">
            <div v-if="loading">
                <b-skeleton-table :rows="8" :columns="4" />
            </div>

            <div v-else-if="lapsNoneReorded" class="sa-empty">
                <p class="mb-3">Aucun tour enregistré pour le moment. Réservez une session et établissez un chrono.</p>
                <b-button variant="primary" :to="{ name: 'reservations' }">Réserver une session</b-button>
            </div>

            <template v-else>
                <div class="sa-toolbar">
                    <b-form class="sa-search" @submit.prevent="getLaps()">
                        <b-icon icon="search" />
                        <input v-model="lapTimeQuery" placeholder="Rechercher une voiture, un circuit, un pneu" />
                    </b-form>
                    <button
                        class="sa-chip"
                        :class="{ 'is-active': validLapsOnly }"
                        @click="
                            validLapsOnly = !validLapsOnly
                            getLaps()
                        "
                    >
                        Valides uniquement
                    </button>
                    <button class="sa-chip" :class="{ 'is-active': !!selectedGroup }" @click="$refs.groupModal.show()">Groupe : {{ selectedGroup || 'Tous' }}</button>
                    <button class="sa-chip" :class="{ 'is-active': !!selectedTrack }" @click="$refs.trackModal.show()">Circuit : {{ selectedTrackNameShort || 'Tous' }}</button>
                    <button v-if="hasFilters" class="sa-chip" @click="clearLapFilters">Effacer</button>
                    <span class="sa-toolbar__spacer"></span>
                    <span v-if="ranked" class="sa-tag">Vue classée</span>
                </div>

                <div v-if="lapsLoading">
                    <b-skeleton-table :rows="6" :columns="4" />
                </div>

                <div v-else-if="lapsResources.length > 0" class="sa-table__wrap">
                    <table class="sa-table">
                        <thead>
                            <tr>
                                <th v-if="ranked">Rang</th>
                                <th>Circuit</th>
                                <th>Tour</th>
                                <th class="d-none d-md-table-cell">Voiture</th>
                                <th class="d-none d-lg-table-cell">Réglages</th>
                                <th>Valide</th>
                                <th class="d-none d-md-table-cell">Enregistré</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(lapTime, i) in lapsResources" :key="i" :class="{ 'sa-table__row--lead': ranked && lapTime.attributes.rank < 4 }">
                                <td v-if="ranked" class="sa-table__mono text-info">{{ String(lapTime.attributes.rank).padStart(2, '0') }}</td>
                                <td class="sa-table__strong">
                                    <a v-if="lapTime.attributes.track" href="javascript:{};" @click.prevent="selectTrackAndGo(lapTime.attributes.track, lapTime.attributes.track_name)">{{ lapTime.attributes.track_name }}</a>
                                    <span v-else>{{ lapTime.attributes.track_name }}</span>
                                </td>
                                <td class="sa-table__mono">{{ lapTime.attributes.hms }}</td>
                                <td class="d-none d-md-table-cell">{{ lapTime.attributes.car_name }}</td>
                                <td class="d-none d-lg-table-cell sa-table__dim">{{ lapMeta(lapTime) }}</td>
                                <td :class="lapTime.attributes.approved ? 'text-info' : 'text-muted'">
                                    <b-icon :icon="lapTime.attributes.approved ? 'patch-check-fill' : 'x-circle'" />
                                </td>
                                <td class="d-none d-md-table-cell sa-table__dim">{{ lapTime.createdPretty() }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-else class="sa-empty">
                    <p class="mb-3">Aucun tour ne correspond à ces filtres.</p>
                    <b-button variant="primary" @click="clearLapFilters">Effacer les filtres</b-button>
                </div>

                <p v-if="!ranked && lapsResources.length" class="sa-mono small text-muted mt-3">Astuce : touchez le nom d’un circuit pour y voir vos tours classés.</p>

                <div v-if="lapsPage && lapsPage.pages > 1" class="sa-pager">
                    <b-button size="sm" variant="outline-primary" :disabled="lapsPage.page == 1" @click="lapTimePrevPage()">Préc.</b-button>
                    <span class="sa-pager__label">{{ pageLabel }}</span>
                    <b-button size="sm" variant="outline-primary" :disabled="lapsPage.page >= lapsPage.pages" @click="lapTimeNextPage()">Suiv.</b-button>
                </div>
            </template>
        </div>

        <b-modal ref="groupModal" title="Filtrer par groupe" size="md" hide-footer no-fade centered>
            <b-list-group>
                <b-list-group-item href="javascript:{}" @click="modalSelectGroup('')">Tous les groupes</b-list-group-item>
                <b-list-group-item v-for="(groupResource, i) in groupResources" :key="i" @click="modalSelectGroup(groupResource.id)" :active="selectedGroup == groupResource.id" href="javascript:{}">{{ groupResource.attributes.name }}</b-list-group-item>
            </b-list-group>
        </b-modal>

        <b-modal ref="trackModal" title="Filtrer par circuit" size="md" hide-footer no-fade centered>
            <b-list-group>
                <b-list-group-item href="javascript:{}" @click="modalSelectTrack('', '')">Tous les circuits</b-list-group-item>
                <b-list-group-item v-for="(trackResource, i) in trackResources" :key="i" @click="modalSelectTrack(trackResource.id, trackResource.attributes.name)" :active="selectedTrack == trackResource.id" href="javascript:{}">{{ trackResource.attributes.name }}</b-list-group-item>
            </b-list-group>
        </b-modal>
    </div>
</template>
